import { ApiError, api } from "./client";
import type { RunSource, RunSourceFilterSpec } from "./types";

export type CreatedRunSource = {
  id: string;
  name: string;
  line_count: number;
  byte_count: number;
};

/**
 * Persist a domain filter as a virtual run source. The filter itself is
 * saved — not the resolved domain set — so a run launched from this
 * source later picks up any new domains matching the filter at run
 * time. Mirrors the four-param shape used by `/api/domains`.
 */
export const createFilterSource = (name: string, filter: RunSourceFilterSpec) =>
  api<CreatedRunSource>("/api/run-sources/filter", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, filter }),
  });

export const listRunSources = () =>
  api<RunSource[]>("/api/run-sources?limit=200");

/**
 * Stream a `File` (or any Blob) up to `/api/run-sources` as the raw body.
 * No multipart wrapper — the server reads `request.body()` directly.
 */
export const uploadRunSource = (name: string, file: Blob) =>
  api<CreatedRunSource>(
    `/api/run-sources?name=${encodeURIComponent(name)}`,
    {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: file,
    },
  );

/**
 * Same as `uploadRunSource`, but uses `XMLHttpRequest` so we can observe
 * upload bytes — `fetch` doesn't expose `upload.onprogress`. `onProgress`
 * receives a 0..1 fraction; it isn't called when the body length isn't
 * computable (rare for Blob/File, but keep callers tolerant of that).
 */
export const uploadRunSourceWithProgress = (
  name: string,
  file: Blob,
  onProgress: (fraction: number) => void,
) =>
  new Promise<CreatedRunSource>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(
      "POST",
      `/api/run-sources?name=${encodeURIComponent(name)}`,
    );
    xhr.setRequestHeader("Content-Type", "text/plain");
    xhr.setRequestHeader("Accept", "application/json");
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () =>
      reject(new ApiError(0, null, "network error"));
    xhr.onload = () => {
      const text = xhr.responseText;
      let body: unknown = text;
      try { body = text ? JSON.parse(text) : null; } catch { /* keep raw */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as CreatedRunSource);
        return;
      }
      const detail =
        typeof body === "object" && body && "detail" in body
          ? (body as { detail: unknown }).detail
          : body;
      const msg = typeof detail === "string" ? detail : `HTTP ${xhr.status}`;
      reject(new ApiError(xhr.status, detail, msg));
    };
    xhr.send(file);
  });

export const runFromSource = (
  sourceId: string,
  options: { forceRescan?: boolean; checkIds?: string[] | null } = {},
) =>
  api<{ run_id: string | null; source_id: string }>(
    `/api/run-sources/${sourceId}/run`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        force_rescan: options.forceRescan ?? false,
        check_ids: options.checkIds ?? null,
      }),
    },
  );

export const deleteRunSource = (sourceId: string) =>
  api<void>(`/api/run-sources/${sourceId}`, { method: "DELETE" });

export type DiskUrlList = {
  date: string;
  filename: string;
  rel_path: string;
  byte_count: number;
  line_count: number;
};

/** List importable URL-list files under the operator's bind-mounted
 * `URL_LISTS_DIR`. Server-side cache means polling is cheap. */
export const listDiskUrlLists = () =>
  api<DiskUrlList[]>("/api/run-sources/disk");

/** Import a disk file as a `kind=file` saved source. Same writer as the
 * browser upload — once imported, the row is identical to any upload. */
export const importDiskUrlList = (relPath: string) =>
  api<CreatedRunSource>("/api/run-sources/disk/import", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rel_path: relPath }),
  });
